import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { LRS_ACTIONS, LRS_OBJECT_TYPES } from '@techchange/components';

import { RESOURCE_TYPES } from '../../../../constants';
import RuleEditorForm from '../../RuleEditorForm';
import getObjectOptionsMap from '../../utils/getObjectOptionsMap';
import getObjectTypeMap from '../../utils/getObjectTypeMap';

const propTypes = {

	/**
	 * The certificate object.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([LRS_OBJECT_TYPES.CERTIFICATE]).isRequired,

	}).isRequired,

	/**
	 * The session object.
	 */
	parent: PropTypes.shape({

		/**
		 * The session id.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The session title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The rule for the resource.
	 */
	rule: PropTypes.shape({

		/**
		 * The condition for the rule.
		 */
		condition: PropTypes.shape({

			/**
			 * The list of subconditions for the condition.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string,

			})),

		}),

	}),

	/**
	 * The list of discussion threads in the session.
	 */
	discussionThreads: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The thread ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The thread title.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * The list of discussion topics in the session.
	 */
	discussionTopics: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The topic ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The topic title.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * The list of form resources in the session.
	 */
	forms: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([RESOURCE_TYPES.FORM]).isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The resource metadata.
		 */
		data: PropTypes.shape({

			/**
			 * The ID of the associated form.
			 */
			form_id: PropTypes.string.isRequired,

		}).isRequired,

	})),

	/**
	 * The list of poll resources in the session.
	 */
	polls: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([RESOURCE_TYPES.POLL]).isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The resource metadata.
		 */
		data: PropTypes.shape({

			/**
			 * The ID of the associated form.
			 */
			form_id: PropTypes.string.isRequired,

		}).isRequired,

	})),

	/**
	 * The list of quiz resources in the session.
	 */
	quizzes: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The resource ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([RESOURCE_TYPES.QUIZ]).isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The resource metadata.
		 */
		data: PropTypes.shape({

			/**
			 * The ID of the associated form.
			 */
			form_id: PropTypes.string.isRequired,

		}).isRequired,

	})),

};

const RuleEditor = (props) => {

	const {
		rule,
		parent,
		discussionThreads,
		discussionTopics,
		forms,
		polls,
		quizzes,
	} = props;

	const intl = useIntl();

	// Defines the list of default completion requirements, i.e. subconditions
	//	for the completion rule
	const initialValues = [
		{
			active: false,
			action: LRS_ACTIONS.COMPLETED,
			object_type: LRS_OBJECT_TYPES.SESSION,
			object_id: parent.id, // The session id
			object_title: parent.title, // The session title
			parent_object_type: null,
			parent_object_id: null,
		},
		{
			active: false,
			action: LRS_ACTIONS.EARNED,
			object_type: LRS_OBJECT_TYPES.POINT,
			meta: {
				threshold: 100,
			},
			object_id: null,
			object_title: null,
			parent_object_type: null,
			parent_object_id: null,
		}
	];

	let customValues = [];

	// Filter out any default subconditions from the existing requirements
	if (rule && rule.condition && rule.condition.subconditions) {
		customValues = rule.condition.subconditions.filter((subcondition) =>
			subcondition.object_type !== LRS_OBJECT_TYPES.SESSION &&
			subcondition.object_type !== LRS_OBJECT_TYPES.POINT);
	}

	let objectTypeOptionsMap = {};

	const objectOptionsMap = getObjectOptionsMap(intl, {
		discussionThreads,
		discussionTopics,
		forms,
		polls,
		quizzes
	});

	// Only allow object types we have object options for
	const objectTypes = Object.keys(objectOptionsMap);

	// Check to make sure that at least one of the valid object types exists
	if (objectTypes.length > 0) {
		objectTypeOptionsMap = getObjectTypeMap(intl, objectTypes);
	}

	return (
		<div>
			<RuleEditorForm
				initialValues={initialValues}
				customValues={customValues}
				objectTypeOptionsMap={objectTypeOptionsMap}
				objectOptionsMap={objectOptionsMap}
				ruleAction={LRS_ACTIONS.EARNED}
				allowCustomRequirements
				{...props} />
		</div>
	);

};

RuleEditor.propTypes = propTypes;

export default RuleEditor;
